import axios from 'axios';

// Define API base URL from environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://hotels.myabad.com';

// Create axios instance with authentication headers
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add authentication interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Types
export interface Property {
  id: number;
  name: string;
  property_id: string;
}

export interface UserProfile {
  id: number;
  role: 'admin' | 'unit_manager';
  properties_detail: Property[];
  is_active: boolean;
  created_at: string;
  created_by: number | null;
  created_by_username: string | null;
}

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  date_joined: string;
  profile: UserProfile | null;
}

export interface CreateUserData {
  username: string;
  password: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role: 'admin' | 'unit_manager';
  property_ids: number[];
}

export interface UpdateUserData {
  email?: string;
  first_name?: string;
  last_name?: string;
  is_active?: boolean;
  role?: 'admin' | 'unit_manager';
  property_ids?: number[];
  password?: string;
}

// User service
const userService = {
  // Get all users
  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/users/');
    return response.data.data;
  },

  // Get a specific user
  getUser: async (id: number): Promise<User> => {
    const response = await api.get(`/users/${id}/`);
    return response.data.data;
  },

  // Create a new user
  createUser: async (userData: CreateUserData): Promise<User> => {
    const response = await api.post('/users/', userData);
    return response.data.data;
  },

  // Update a user
  updateUser: async (id: number, userData: UpdateUserData): Promise<User> => {
    const response = await api.put(`/users/${id}/`, userData);
    return response.data.data;
  },

  // Delete a user
  deleteUser: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}/`);
  },

  // Get all properties for dropdown
  getProperties: async (): Promise<Property[]> => {
    const response = await api.get('/api/properties/');
    return response.data.data;
  }
};

export default userService;

